/**
 * Where a newly-created or imported asset should be parented.
 *
 * The editor has three "active container" contexts that capture new assets so they
 * join the subtree the user is working in — Pages (active page), Keyframes
 * (container), and Properties state groups (the opened state context). This module
 * holds the PURE decision logic — precedence + the state-context gate — separated
 * from index.ts's imperative scene wiring so it can be unit-tested directly. The
 * host resolves each candidate (which needs scene/overlay state) and passes them in.
 */

import type { SceneObject } from 'trace-viewer/scene';

/** Minimal view of the state-context overlay this decision depends on. */
export interface StateContextLike {
    /** The active state-edit context while editing is on, else null. */
    getActiveContext: () => SceneObject | null;
    /** True when the active context is one the user actually OPENED (has states
     *  or was explicitly opened) — only then should assets nest under it. */
    isStateContext: () => boolean;
}

/** How `resolveStateParent` resolves the context when the overlay isn't mounted. */
export interface StateFallback {
    /** Whether States Editing mode is globally on. */
    enabled: boolean;
    /** The current selection (or null). */
    selected: SceneObject | null;
    /** Resolve the context for a selection (the overlay's resolveContext). */
    resolveContext: (selected: SceneObject) => SceneObject | null;
}

/**
 * The state-group context to nest assets under, or null.
 *
 * Prefers the mounted overlay: nest under its active context ONLY when it is a
 * real opened context (`isStateContext`). A bare object that merely defaulted to
 * itself while States Editing is globally on must NOT capture asset-parenting — or
 * every add/import would nest under whatever plain mesh happens to be selected.
 *
 * When the overlay isn't mounted, fall back to resolving from the selection, but
 * only nest under an existing state GROUP strictly ABOVE the selection
 * (resolveContext returns an ancestor, not the selection itself) — never a bare
 * selection.
 */
export function resolveStateParent(
    overlay: StateContextLike | null,
    fallback: StateFallback,
): SceneObject | null {
    if (overlay) {
        return overlay.isStateContext() ? overlay.getActiveContext() : null;
    }
    if (!fallback.enabled || !fallback.selected) return null;
    const resolved = fallback.resolveContext(fallback.selected);
    return resolved && resolved !== fallback.selected ? resolved : null;
}

/** The four parenting candidates, most-specific first. */
export interface ParentCandidates {
    /** Active page when the selection is inside a Pages container. */
    activePage: SceneObject | null;
    /** Keyframes container when the selection is inside one. */
    keyframeContainer: SceneObject | null;
    /** Opened state-group context (see {@link resolveStateParent}). */
    stateRoot: SceneObject | null;
    /**
     * The document root — the file's own root node and the default home for
     * top-level objects (SCENE_ROOT_SPEC D8). Never the engine mount point.
     */
    documentRoot: SceneObject | null;
}

/**
 * Pick the parent for a NEW object: the most-specific active context, else the
 * document root. Pages → Keyframes → State group → document root.
 */
export function resolveNewObjectParent(c: ParentCandidates): SceneObject | null {
    return c.activePage ?? c.keyframeContainer ?? c.stateRoot ?? c.documentRoot ?? null;
}

/**
 * Pick the parent for an IMPORTED object. Same precedence as new objects but WITHOUT
 * the document-root fallback: the viewer already mounts an import under the session's
 * document root, so an import with no active context is left where the loader put it
 * — a null result means "don't reparent".
 */
export function resolveImportParent(c: Omit<ParentCandidates, 'documentRoot'>): SceneObject | null {
    return c.activePage ?? c.keyframeContainer ?? c.stateRoot ?? null;
}
